import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Trophy, Star, Flame, Award, GraduationCap, Zap, Crown,
  Medal, Calculator, BookOpen, FlaskConical, Gamepad2, Lock,
  Brain, Target, Puzzle, PenTool, FileText, BookMarked, Sparkles, TrendingUp
} from 'lucide-react';

const AchievementsPage = () => {
  const { user, api } = useAuth();
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const fetchAchievements = async () => {
      try {
        const response = await api.get('/achievements');
        setAchievements(response.data.achievements || []);
      } catch (error) {
        console.error('Error fetching achievements:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAchievements();
  }, [api]);

  const iconMap = {
    Star: Star,
    Zap: Zap,
    Trophy: Trophy,
    GraduationCap: GraduationCap,
    Crown: Crown,
    Calculator: Calculator,
    BookOpen: BookOpen,
    FlaskConical: FlaskConical,
    Award: Award,
    Flame: Flame,
    Fire: Flame,
    Medal: Medal,
    Gamepad2: Gamepad2,
    Brain: Brain,
    Target: Target,
    Puzzle: Puzzle,
    PenTool: PenTool,
    FileText: FileText,
    BookMarked: BookMarked,
    Sparkles: Sparkles,
    TrendingUp: TrendingUp
  };

  // Categorize achievements
  const categories = {
    all: { name: 'All Badges', icon: Trophy, color: 'amber' },
    lessons: { name: 'Learning', icon: BookOpen, color: 'purple' },
    games: { name: 'Games', icon: Gamepad2, color: 'pink' },
    essays: { name: 'Writing', icon: PenTool, color: 'blue' },
    streaks: { name: 'Streaks', icon: Flame, color: 'orange' }
  };

  const getCategoryForAchievement = (achievement) => {
    const id = achievement.id;
    if (id.includes('essay') || id.includes('author') || id.includes('writer')) return 'essays';
    if (id.includes('game') || id.includes('memory') || id.includes('quiz') || 
        id.includes('ninja') || id.includes('spelling') || id.includes('word') || 
        id.includes('scientist') || id.includes('scorer') || id.includes('all-rounder')) return 'games';
    if (id.includes('streak')) return 'streaks';
    return 'lessons';
  };

  const filteredAchievements = activeCategory === 'all' 
    ? achievements 
    : achievements.filter(a => getCategoryForAchievement(a) === activeCategory);

  const unlockedCount = achievements.filter(a => a.unlocked).length;
  const totalPoints = achievements.filter(a => a.unlocked).reduce((sum, a) => sum + a.points, 0);

  return (
    <StudentLayout>
      <div data-testid="achievements-page">
        {/* Header */}
        <div className="student-card bg-amber-100 border-amber-300 mb-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-amber-500 rounded-2xl border-2 border-gray-800 flex items-center justify-center animate-float">
                <Trophy className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold text-slate-800">My Badges</h1>
                <p className="text-slate-600">Collect badges by completing challenges!</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="font-heading text-3xl font-bold text-amber-600">{unlockedCount}</p>
                <p className="text-sm text-slate-600">Badges Earned</p>
              </div>
              <div className="text-center">
                <p className="font-heading text-3xl font-bold text-purple-600">{totalPoints}</p>
                <p className="text-sm text-slate-600">Bonus Points</p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="student-card mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold text-slate-800">Badge Progress</h2>
            <span className="text-slate-600">{unlockedCount} / {achievements.length}</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-4 border-2 border-gray-800 overflow-hidden">
            <div 
              className="bg-amber-400 h-full transition-all duration-500"
              style={{ width: `${achievements.length > 0 ? (unlockedCount / achievements.length) * 100 : 0}%` }}
            />
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {Object.entries(categories).map(([key, cat]) => {
            const CategoryIcon = cat.icon;
            const categoryCount = key === 'all' 
              ? achievements.length 
              : achievements.filter(a => getCategoryForAchievement(a) === key).length;
            const unlockedInCategory = key === 'all'
              ? unlockedCount
              : achievements.filter(a => getCategoryForAchievement(a) === key && a.unlocked).length;
            
            return (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold transition-all ${
                  activeCategory === key 
                    ? `bg-${cat.color}-500 text-white` 
                    : 'bg-white border-2 border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
                data-testid={`category-${key}`}
              >
                <CategoryIcon className="w-4 h-4" />
                {cat.name}
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  activeCategory === key ? 'bg-white/20' : 'bg-slate-100'
                }`}>
                  {unlockedInCategory}/{categoryCount}
                </span>
              </button>
            );
          })}
        </div>

        {/* Achievements Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-600 border-t-transparent"></div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredAchievements.map(achievement => {
              const IconComponent = iconMap[achievement.icon] || Award;
              const category = getCategoryForAchievement(achievement);
              const catColor = categories[category]?.color || 'amber';
              
              return (
                <div 
                  key={achievement.id}
                  className={`student-card text-center relative transition-all hover:scale-105 ${!achievement.unlocked ? 'opacity-60 grayscale' : ''}`}
                  data-testid={`achievement-${achievement.id}`}
                >
                  {!achievement.unlocked && (
                    <div className="absolute top-3 right-3">
                      <Lock className="w-4 h-4 text-slate-400" />
                    </div>
                  )}
                  
                  <div className={`w-14 h-14 mx-auto mb-3 rounded-2xl border-2 border-gray-800 flex items-center justify-center ${
                    achievement.unlocked 
                      ? `bg-${catColor}-400` 
                      : 'bg-slate-200'
                  }`}>
                    <IconComponent className={`w-7 h-7 ${achievement.unlocked ? 'text-white' : 'text-slate-400'}`} />
                  </div>
                  
                  <h3 className="font-heading font-bold text-slate-800 text-sm mb-1">{achievement.name}</h3>
                  <p className="text-xs text-slate-500 mb-2 line-clamp-2">{achievement.description}</p>
                  
                  <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                    achievement.unlocked 
                      ? `bg-${catColor}-100 text-${catColor}-700` 
                      : 'bg-slate-100 text-slate-500'
                  }`}>
                    <Star className="w-3 h-3" />
                    +{achievement.points}
                  </div>
                  
                  {achievement.unlocked && achievement.unlocked_at && (
                    <p className="text-xs text-slate-400 mt-2">
                      {new Date(achievement.unlocked_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {filteredAchievements.length === 0 && !loading && (
          <div className="student-card text-center py-8">
            <div className="text-4xl mb-3">🏆</div>
            <p className="text-slate-500">No badges in this category yet!</p>
          </div>
        )}

        {/* Encouragement */}
        <div className="mt-8 student-card bg-purple-100 border-purple-300 text-center">
          <div className="text-4xl mb-3">🌟</div>
          <h3 className="font-heading text-lg font-bold text-slate-800 mb-2">
            Keep Going, {user?.first_name}!
          </h3>
          <p className="text-slate-600">
            Complete lessons, play games, write essays, and maintain your streak to unlock more badges!
          </p>
        </div>
      </div>
    </StudentLayout>
  );
};

export default AchievementsPage;
